import InputAdornment from '@/components/Inputs/InputAdornment';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CouponSvg from '@/images/Coupon.svg';
import InfoIcon from '@/images/Info.svg';
import { useAnalytics } from '@/services/analytics';
import { getCouponDetails } from '@/services/api';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { ICouponData, PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { Clear } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { useState } from 'react';
import LoadingAnimation from '../../../images/LoadingAnimation.json';

const LoadingSmall = styled(Player)`
    height: 3rem;
    aspect-ratio: 1;
`;

const PromotionalContainer = styled.div`
    align-self: self-start;
`;
const PromoLabel = styled.label<{ show?: boolean; pointer?: boolean; error?: boolean }>`
    display: ${props => (props.show === false ? 'none' : 'flex')};
    cursor: ${props => (props.pointer ? 'pointer' : 'default')};
    color: ${props => (props.error ? theme.colors.error[400] : theme.colors.primary)};
    font-family: inherit;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 600;
    line-height: 18px;
    min-height: ${props => (props.error || props.show === false ? 'auto' : '4rem')};
    align-items: center;
`;
const StyledInputAdornment = styled(InputAdornment)`
    max-width: 230px;
    min-width: 230px;
`;
const ChipContainer = styled.div<{ show: boolean }>`
    display: ${props => (props.show ? 'flex' : 'none')};
    flex-direction: column;
    color: ${theme.colors.black[500]};
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    gap: 4px;
`;
const StyledChip = styled(Chip)`
    font-size: 1.6rem;
    display: flex;
    border-radius: 8px;
    padding: 4px 0;
    box-sizing: content-box;
    font-weight: normal;
`;

interface Props {
    setCoupon: (coupon: ICouponData['coupon'] | null) => void;
    coupon: ICouponData['coupon'] | null;
    isOldCoupon: boolean;
    priceId: string;
    plan: PlanGroup;
    promoCode: string | null;
    setPromoCode: (_value: string | null) => void;
}

export const Promotion = ({ setCoupon, coupon, isOldCoupon, priceId, plan, promoCode, setPromoCode }: Props) => {
    const mixpanel = useAnalytics();

    const [couponError, setCouponError] = useState<string>('');
    const [appliedCoupon, setAppliedCoupon] = useState<boolean>(false);

    const [applyingCoupon, setApplyingCoupon] = useState(false);

    const applyCoupon = async () => {
        if (promoCode) {
            setApplyingCoupon(true);

            getCouponDetails(promoCode, priceId)
                .then(({ coupon, attempts }) => {
                    setCoupon(coupon);
                    setAppliedCoupon(true);
                    mixpanel?.track('promo-code', { event: 'success', code: promoCode, attempts, planGroup: plan, priceId });
                })
                .catch(e => {
                    logger.error('Error fetching coupon details', { e });
                    const error = e.response?.data;

                    if (error?.kind === 'CouponError') {
                        setCouponError(error.description);
                        mixpanel?.track('promo-code', {
                            event: 'error',
                            error: error.description,
                            code: promoCode,
                            priceId,
                            planGroup: plan,
                            attempts: error.attempts,
                        });
                    } else {
                        setCouponError('Error fetching coupon details, please try again later');
                    }
                })
                .finally(() => setApplyingCoupon(false));
        }
    };
    const getAdornment = () => {
        if (promoCode || isOldCoupon) {
            if (applyingCoupon || isOldCoupon) {
                return <LoadingSmall src={LoadingAnimation} autoplay loop />;
            } else if (couponError) {
                return <MaskedIcon src={InfoIcon} color={theme.colors.error[400]} size="1.6rem" />;
            } else {
                return (
                    <PromoLabel pointer onClick={applyCoupon}>
                        Apply
                    </PromoLabel>
                );
            }
        }
    };

    return (
        <PromotionalContainer>
            <PromoLabel show={promoCode === null && !coupon} onClick={() => setPromoCode('')}>
                Have a promotion code?
            </PromoLabel>
            <StyledInputAdornment
                show={promoCode !== null && (!appliedCoupon || !!couponError || isOldCoupon)}
                fullWidth
                error={!!couponError}
                size="small"
                value={promoCode ?? ''}
                onChange={e => (setPromoCode(e.currentTarget.value), setCoupon(null), setCouponError(''))}
                adornmentPosition="end"
                placeholder="Enter promotion code">
                {getAdornment()}
            </StyledInputAdornment>
            <PromoLabel show={!!couponError} error>
                {couponError}
            </PromoLabel>
            <ChipContainer show={!couponError && !!coupon && !isOldCoupon}>
                {promoCode && (
                    <StyledChip
                        onDelete={() => (setCoupon(null), setPromoCode(null), setAppliedCoupon(false))}
                        label={coupon?.promoCode}
                        icon={<MaskedIcon color={theme.colors.black[300]} src={CouponSvg} size="16px" />}
                        deleteIcon={<Clear />}
                    />
                )}
                {coupon?.name}
            </ChipContainer>
        </PromotionalContainer>
    );
};
